import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { api } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { CheckCircle2, Clock, AlertTriangle, XCircle, Shield } from 'lucide-react';

interface Reviewer {
  user_id: string;
  name: string;
  email: string;
  recommendation: string | null;
  recommended_at: string | null;
}

interface QuorumData {
  total_reviewers: number;
  recommendations_received: number;
  quorum_met: boolean;
  reviewers: Reviewer[];
}

interface CommitteeDecisionPanelProps {
  proposalId: string;
  proposalStatus: string;
  submitterName?: string;
  onUpdated: () => void;
}

const REC_CONFIG: Record<string, { label: string; icon: typeof CheckCircle2; color: string; badgeVariant: 'success' | 'destructive' | 'warning' | 'default' }> = {
  approve: { label: 'Approved', icon: CheckCircle2, color: 'text-green-600', badgeVariant: 'success' },
  approve_with_conditions: { label: 'Approved with Conditions', icon: AlertTriangle, color: 'text-amber-600', badgeVariant: 'warning' },
  needs_more_info: { label: 'Needs More Info', icon: Clock, color: 'text-blue-600', badgeVariant: 'default' },
  reject: { label: 'Rejected', icon: XCircle, color: 'text-red-600', badgeVariant: 'destructive' },
};

function allRecommendationsApprove(quorum: QuorumData): boolean {
  if (!quorum.quorum_met) return false;
  return quorum.reviewers.every((r) =>
    r.recommendation === 'approve' || r.recommendation === 'approve_with_conditions'
  );
}

export function CommitteeDecisionPanel({ proposalId, proposalStatus, submitterName, onUpdated }: CommitteeDecisionPanelProps) {
  const { user } = useAuthStore();
  const [quorum, setQuorum] = useState<QuorumData | null>(null);
  const [myRec, setMyRec] = useState<string>('');
  const [myText, setMyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isReviewer = user?.roles.some((r) => ['reviewer', 'chair', 'admin'].includes(r));
  const isDecisionStage = proposalStatus === 'in_review' || proposalStatus === 'awaiting_decision';

  useEffect(() => {
    const fetchQuorum = async () => {
      try {
        const res = await api.get<QuorumData>(`/comments/recommendations/${proposalId}`);
        setQuorum(res);
        const mine = res.reviewers?.find((r) => r.user_id === user?.id);
        if (mine?.recommendation) setMyRec(mine.recommendation);
      } catch (err) {
        console.error(err);
      }
    };
    if (isDecisionStage) fetchQuorum();
  }, [proposalId, proposalStatus, isDecisionStage, user?.id]);

  const submitRecommendation = async () => {
    if (!myRec) return;
    if (myRec !== 'approve' && !myText.trim()) {
      setError('Please provide details for your recommendation.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.post('/comments', {
        proposal_id: proposalId,
        body: myText || 'Approved',
        recommendation: myRec,
        conditions: myRec === 'approve_with_conditions' ? myText : undefined,
      });
      setMyRec('');
      setMyText('');
      onUpdated();
      const res = await api.get<QuorumData>(`/comments/recommendations/${proposalId}`);
      setQuorum(res);
      const mine = res.reviewers?.find((r) => r.user_id === user?.id);
      if (mine?.recommendation) setMyRec(mine.recommendation);
    } catch (err: any) {
      setError(err.message || 'Failed to submit');
    }
    setSubmitting(false);
  };

  if (!isDecisionStage || !quorum) return null;

  const myReviewerEntry = quorum.reviewers?.find((r) => r.user_id === user?.id);
  const hasSubmitted = !!myReviewerEntry?.recommendation;
  const allApproved = allRecommendationsApprove(quorum);

  return (
    <Card className="border-2 border-primary/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            Committee Decision
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant={quorum.quorum_met ? 'success' : 'warning'}>
              {quorum.recommendations_received}/{quorum.total_reviewers} Reviewed
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {allApproved && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800 font-medium">
              {proposalStatus === 'awaiting_decision'
                ? 'All reviewers approved — proposal is moving to the deployment stage. Refresh if status has not updated yet.'
                : 'All reviewers approved — proposal moved to deployment stage.'}
            </p>
            {proposalStatus === 'awaiting_decision' && submitterName && (
              <p className="text-sm text-green-700 mt-1">
                Waiting for {submitterName} to submit deployment details (HLD, repo, etc.).
              </p>
            )}
          </div>
        )}

        <div className="space-y-2">
          {quorum.reviewers?.map((reviewer) => {
            const rec = reviewer.recommendation;
            const config = rec ? REC_CONFIG[rec] : null;
            const Icon = config?.icon || Clock;
            const isMe = reviewer.user_id === user?.id;

            return (
              <div
                key={reviewer.user_id}
                className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                  rec ? 'bg-muted/30' : 'bg-amber-50/50 border-amber-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                    rec ? 'bg-primary/10 text-primary' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {reviewer.name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {reviewer.name}
                      {isMe && <span className="text-xs text-muted-foreground ml-1">(You)</span>}
                    </p>
                    {rec && reviewer.recommended_at && (
                      <p className="text-xs text-muted-foreground">
                        {new Date(reviewer.recommended_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {rec ? (
                    <Badge variant={config?.badgeVariant || 'default'} className="gap-1">
                      <Icon className="h-3 w-3" />
                      {config?.label}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-amber-700 border-amber-300 gap-1">
                      <Clock className="h-3 w-3" />
                      Pending
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {isReviewer && !allApproved && isDecisionStage && (
          <>
            <Separator />
            <div className="space-y-3">
              <Label className="text-sm font-semibold">
                {hasSubmitted ? 'Update Your Recommendation' : 'Your Recommendation'}
              </Label>
              {hasSubmitted && (
                <p className="text-xs text-muted-foreground">Your latest recommendation is shown above. Submit again to update it.</p>
              )}
              <div className="flex flex-wrap gap-2">
                {Object.entries(REC_CONFIG).map(([key, config]) => (
                  <Button
                    key={key}
                    type="button"
                    variant={myRec === key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setMyRec(key); setError(''); }}
                    className="gap-1.5"
                  >
                    <config.icon className="h-3.5 w-3.5" />
                    {config.label}
                  </Button>
                ))}
              </div>
              {myRec && (
                <Textarea
                  value={myText}
                  onChange={(e) => setMyText(e.target.value)}
                  rows={3}
                  placeholder={
                    myRec === 'approve' ? 'Rationale (optional)...' :
                    myRec === 'approve_with_conditions' ? 'List the conditions...' :
                    myRec === 'needs_more_info' ? 'What questions do you have?' :
                    'Reason for rejection...'
                  }
                />
              )}
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="flex justify-end">
                <Button size="sm" onClick={submitRecommendation} disabled={!myRec || submitting}>
                  {submitting ? 'Submitting...' : hasSubmitted ? 'Update Recommendation' : 'Submit Recommendation'}
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
